import { useMemo } from 'react'
import { getCapacityValue } from '../components/product-showcase/showcase-utils'
import { useBuildings } from '../components/buildings-provider'
import { ProductType } from '../types/product'
import { useQueryParams } from './useQueryParams'
import { parseProductQuery } from '../components/product-showcase/product-query'

export const useShowcasePathInfo = ({ useProductParam = false }: { useProductParam?: boolean } = {}) => {
  const params = useQueryParams()
  const { getBuildingByKey } = useBuildings()

  const { types, type } = useMemo(() => {
    const productTypes = useProductParam ? parseProductQuery(params.query.product as string | undefined) : undefined
    const typeParam = (params.query.type as string | undefined) ?? undefined
    // Parse comma-separated types or use single type (backward compatible)
    const parsedTypes = typeParam ? (typeParam.includes(',') ? typeParam.split(',').filter(Boolean) : [typeParam]) : []
    const resolvedTypes = productTypes ?? (parsedTypes.length > 0 ? (parsedTypes as ProductType[]) : undefined)
    const firstType = resolvedTypes?.[0]
    return {
      types: resolvedTypes,
      type: firstType as ProductType | undefined,
    }
  }, [params.query.product, params.query.type, useProductParam])

  const capacity = useMemo(() => {
    const rawCapacity = params.query.capacity as string | undefined
    return rawCapacity ? getCapacityValue((type as ProductType) ?? 'meeting_room', rawCapacity) : undefined
  }, [params.query.capacity, type])

  const building = useMemo(() => params.query.building as string | undefined, [params.query.building])
  const region = useMemo(() => {
    const locationRegion = (params.query.location as string | undefined) ?? undefined
    const regionFromBuilding = building ? getBuildingByKey(building)?.city.key : undefined
    return regionFromBuilding ?? locationRegion
  }, [params.query.location, building, getBuildingByKey])

  return {
    region,
    building: building ?? undefined,
    type,
    types,
    capacity: capacity?.value,
  }
}
